<script setup>
import axios from "@axios"
import {useUsersStore} from '@/stores'

const usersStore = useUsersStore()

let importer = ref(null)

let issuesByRow = ref([])
let missingRestaurants = ref([])
let missingMembers = ref([])

let bookingsInserted = ref(0)

let importAttempted = ref(false)

let createMembersIfEmailDoesNotExist = ref(true)
let favoriteRestaurantWhenInsertingBooking = ref(true)
let noInsertsOrUpdates = ref(false)

const loadScript = src => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Script load error for ${src}`))
    document.head.appendChild(script)
  })
}

const openImporter = () => {
  if (importer.value) {
    importer.value.showModal()
  }
}


const uploadData = async data => {
  await usersStore.showLoadingOverlay()
  importAttempted.value = true
  try {
    data.createMembersIfEmailDoesNotExist = createMembersIfEmailDoesNotExist.value
    data.favoriteRestaurantWhenInsertingBooking = favoriteRestaurantWhenInsertingBooking.value
    data.noInsertsOrUpdates = noInsertsOrUpdates.value

    let res = await axios.post(`/reservation/import`, data)
    if(res.data.issuesByRow){
      issuesByRow.value = res.data.issuesByRow
    }
    if(res.data.missingRestaurants){
      missingRestaurants.value = res.data.missingRestaurants
    }
    if(res.data.missingMembers){
      missingMembers.value = res.data.missingMembers
    }

    if(res.data.bookingsInserted){
      bookingsInserted.value = res.data.bookingsInserted
    }

    if(Object.keys(issuesByRow.value).length > 0){
    //generate csv file with only rows from issuesByRow
      let csvContent = "data:text/csv;charset=utf-8,"
      csvContent += "Original Row Number, Issue"

      let orderedColumnTitles = []

      data.columns.forEach((column, index) => {
        csvContent += `,${column.name}`
        orderedColumnTitles.push(column.name)
      })
      csvContent += "\n"

      data.rows.forEach((row, index) => {
        if(issuesByRow.value[index]){
          csvContent += `${index + 2},"${issuesByRow.value[index]}"`
          orderedColumnTitles.forEach((title, index) => {
            csvContent += `,"${row.values[title]}"`
          })
          csvContent += `\n`
        }
      })
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", "issuesByRow.csv")
      document.body.appendChild(link) // Required for FF
      link.click()
      document.body.removeChild(link)
    }



  } catch(error){
    alert(`There was an error`)
    console.log(error)
    
    return
  }
  await usersStore.hideLoadingOverlay()
}


onMounted(async () => {
  try {
    await loadScript('/libs/csv-import.js')
    importer.value = window.CSVImporter.createCSVImporter({
      domElement: document.getElementById('csv-importer'),
      modalOnCloseTriggered: () => importer.value.closeModal(),
      onComplete: data => {console.log(data); importer.value.closeModal(); uploadData(data)},
      darkMode: false,

      // isModal:false,
      template: {
        columns: [
          {
            name: 'Member Name',
            key: 'fullName',
            required: false,
            description: 'Full name of member (only required when "Create Members that don\'t match emails" is selected)',
            suggested_mappings: ['name', 'fullName', 'Member Name'],
          },
          {
            name: 'Host Member Email',
            key: 'hostEmail',
            required: true,
            description: 'Email address of the host member - must match email used for RM account',
            suggested_mappings: ['email', 'Email Address', 'hostEmail'],
          },
          {
            name: 'Date',
            key: 'date',
            required: true,
            description: 'Date that reservation took place',
            suggested_mappings: ['date'],
          },
          {
            name: 'Restaurant Name',
            key: 'confirmedRestaurant',
            required: true,
            description: 'Name of restaurant. Must match RM name exactly.',
            suggested_mappings: ['restaurant', 'confirmedRestaurant'],
          },
          {
            name: 'Restaurant Market',
            key: 'market',
            required: true,
            description: 'Market/City of restaurant. Must match RM market name exactly.',
            suggested_mappings: ['market'],
          },
          {
            name: 'Time',
            key: 'confirmedTime',
            required: false,
            description: 'Time of reservation in 12hr am/pm format',
            suggested_mappings: ['time', 'confirmedTime'],
          },
          {
            name: 'Party Size',
            key: 'partySize',
            required: true,
            description: 'Number of people that attended',
            suggested_mappings: ['partySize', 'party'],
            data_type: "number",
          },
          {
            name: 'Guaranteed F&B Spend (pp)',
            key: 'minSpendPerPerson',
            required: false,
            description: 'The min F&B spend per person the member promised to spend (in US dollars)',
            suggested_mappings: ['minSpendPerPerson', 'F&B'],
            data_type: "number",
          },
          {
            name: 'Actual Total Spend',
            key: 'actualSpendTotal',
            required: false,
            description: 'The total amount spent at the reservation, including tax and tip (in US dollars)',
            suggested_mappings: ['actualSpendTotal', 'totalSpend', 'Total Spend'],
            data_type: "number",
          },
        ],
      },
    })
  } catch (error) {
    console.error('Failed to load CSV Importer script:', error)
  }
})
</script>

<template>
  <div>
    <div class="ma-6">
      <div v-if="importAttempted">
        Refresh this page to upload again
      </div>
      <VBtn
        :disabled="importAttempted"
        @click="openImporter"
      >
        Import as Direct Booking(s)
      </VBtn><br>
      <VCheckbox
        v-model="noInsertsOrUpdates"
        :disabled="importAttempted"
        label="Test Mode (no inserts or updates actually made)"
      /><br>
      <VCheckbox
        v-model="createMembersIfEmailDoesNotExist"
        :disabled="importAttempted"
        label="Create Members that don't match emails"
      /><br>
      <VCheckbox
        v-model="favoriteRestaurantWhenInsertingBooking"
        :disabled="importAttempted"
        label="Favorite Restaurant when inserting booking"
      />
      <div id="csv-importer" />
    </div>
    <div
      v-if="importAttempted"
      class="ma-6"
    >
      <div v-if="bookingsInserted">
        <h2>{{ bookingsInserted }} bookings Inserted</h2>
      </div>
      <div v-if="Object.keys(issuesByRow).length > 0">
        <h2>{{ Object.keys(issuesByRow).length }} bookings had errors and were not inserted</h2>
        <div>A csv file with these lines has been downloaded to your device for correction</div>
        <ul>
          <li v-for="(issues, index) in issuesByRow">
            Line: {{ parseInt(index)+1 }} - {{ issues }}
          </li>
        </ul>
      </div>

      <div
        v-if="missingRestaurants?.length > 0"
        class="mt-6"
      >
        <h2>Missing Restaurants Summary</h2>
        <ul>
          <li v-for="issue in missingRestaurants">
            {{ issue }}
          </li>
        </ul>
      </div>

      <div v-if="missingMembers?.length > 0">
        <h2>missingMembers</h2>
        <ul>
          <li v-for="issue in missingMembers">
            {{ issue }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
ol{
  margin-left: 2rem;
}

ol li{
  list-style-type: decimal;
}

ul {
  margin-left: 2em;
}

ul li{
  list-style-type: circle;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
